/**
 * Click-driven desktop mega menu.
 *
 * The stock header opens its submenus on pointerenter, via the on:pointerenter
 * bindings in blocks/_header-menu.liquid and the activate/deactivate pair in
 * header-menu.js. Those bindings are removed for the desktop menu, so
 * HeaderMenu never activates and this file owns open/close instead.
 *
 * Visibility rides the theme's own CSS, which keys off aria-expanded on the
 * top-level link, and the panel paints its own background (see the {% style %}
 * block in blocks/_header-menu.liquid), and drives the header underlay inline
 * to paint the row above it -- see cover(). Neither depends on the theme's
 * --full-open-header-height plumbing.
 *
 * Desktop only: below 750px the header renders the drawer, which owns its own
 * behaviour, so every click handler here returns early at that width.
 */

const DESKTOP = '(min-width: 750px)';

class FcMegaMenu {
  constructor(root) {
    this.root = root;
    this.onDocumentClick = this.onDocumentClick.bind(this);
    this.onKeydown = this.onKeydown.bind(this);

    root.addEventListener('click', (event) => this.onClick(event));
    document.addEventListener('click', this.onDocumentClick);
    document.addEventListener('keydown', this.onKeydown);
    // ponytail: closing beats recomputing geometry on every resize frame.
    window.addEventListener('resize', () => this.closeAll());
  }

  onClick(event) {
    // Checked per click rather than once at startup: a single startup check
    // fails permanently for any load where the viewport is not measurable yet
    // (a background tab, a window opened narrow and then maximised), and never
    // re-runs. Below 750px the header renders the drawer, which owns its own
    // behaviour, so there is nothing for this to do.
    if (!window.matchMedia(DESKTOP).matches) return;

    // One click, one decision. If a second controller is ever attached to this
    // element -- the header is hydrated through morph.js, which patches it in
    // place -- both would otherwise act on the same click, and the second would
    // read the state the first had just written and undo it.
    if (event.fcMegaHandled) return;
    event.fcMegaHandled = true;

    const select = event.target.closest('[data-fc-mega-select]');
    if (select) {
      // An L2 without children is a real collection link; let it navigate.
      if (!select.hasAttribute('data-fc-mega-navigates')) {
        event.preventDefault();
        this.selectPane(select);
      }
      return;
    }

    const link = event.target.closest('.menu-list__link');
    if (!link) return;

    const item = link.closest('.menu-list__list-item');
    const submenu = item?.querySelector('.menu-list__submenu');
    if (!submenu) return;

    // Top-level items that open a panel point at # in the menu, so following
    // the href would only dirty the URL.
    event.preventDefault();

    // Read from the DOM rather than a remembered element. morph.js can replace
    // these nodes, which left a stored reference pointing at a detached copy --
    // the comparison that decides open-vs-close then answered against a node
    // that was no longer on the page. Clicking a different top-level item now
    // always switches to it; only clicking the open one closes.
    const wasOpen = link.getAttribute('aria-expanded') === 'true';

    this.closeAll();
    if (!wasOpen) this.openPanel(link, submenu);
  }

  openPanel(link, submenu) {
    // Left behind by the stock hover machinery in header-menu.js if it ever
    // ran. The CSS neutralises it, but there is no reason to carry the flag.
    delete link.dataset.safetyBox;
    link.setAttribute('aria-expanded', 'true');
    submenu.dataset.active = '';
    this.stretch(submenu);
    this.cover(submenu, true);
  }

  /**
   * Paints the header row above the panel.
   *
   * The row cannot carry a background at all: sections/header.liquid sets
   * "background-color: transparent !important" on .header__row, which beats
   * even an inline style. The theme paints it with .header__underlay-open
   * instead, sized from --full-open-header-height by the hover activation this
   * menu replaced.
   *
   * So the underlay is driven directly, inline, which nothing in the theme
   * overrides -- no CSS variables, no cascade to lose. The colour is read off
   * the panel itself, so the row always matches whatever the panel is painted.
   */
  cover(submenu, show) {
    const header = document.getElementById('header-component');
    const underlay = header?.querySelector('.header__underlay-open');
    if (!underlay) return;

    if (!show) {
      underlay.style.height = '';
      underlay.style.background = '';
      return;
    }

    const inner = submenu.querySelector('.menu-list__submenu-inner');
    underlay.style.height = header.getBoundingClientRect().height + 'px';
    underlay.style.background = getComputedStyle(inner).backgroundColor;
  }

  /**
   * Spans the panel across the window.
   *
   * The theme sizes it with width:100%, which resolves against the nearest
   * positioned ancestor -- the centred nav pill, 279px on a 1440px header, not
   * the header row. That collapsed the panel to pill width and clipped its
   * contents against the inner element's overflow-x:hidden.
   *
   * Measured from the panel's own offsetParent rather than assuming which
   * element that is, so it does not matter what the theme positions. Width
   * comes from clientWidth, which excludes the scrollbar -- 100vw does not, and
   * only product templates clip horizontal overflow.
   */
  stretch(submenu) {
    const origin = submenu.offsetParent;
    if (!origin) return;

    submenu.style.left = -origin.getBoundingClientRect().left + 'px';
    submenu.style.width = document.documentElement.clientWidth + 'px';
  }

  /** Closes whatever is open, without needing to know what that was. */
  closeAll() {
    for (const link of this.root.querySelectorAll('.menu-list__link[aria-expanded="true"]')) {
      link.setAttribute('aria-expanded', 'false');
    }
    for (const panel of this.root.querySelectorAll('.menu-list__submenu[data-active]')) {
      delete panel.dataset.active;
      panel.style.left = '';
      panel.style.width = '';
      this.cover(panel, false);
    }
  }

  isOpen() {
    return Boolean(this.root.querySelector('.menu-list__link[aria-expanded="true"]'));
  }

  selectPane(trigger) {
    const mega = trigger.closest('[data-fc-mega]');
    if (!mega) return;

    const index = trigger.dataset.fcMegaSelect;

    for (const other of mega.querySelectorAll('[data-fc-mega-select]')) {
      if (other.hasAttribute('aria-selected')) {
        other.setAttribute('aria-selected', String(other === trigger));
      }
      other.classList.toggle('fc-mega__l2--active', other === trigger);
    }

    for (const pane of mega.querySelectorAll('[data-fc-mega-pane]')) {
      pane.hidden = pane.dataset.fcMegaPane !== index;
    }
  }

  onDocumentClick(event) {
    if (this.root.contains(event.target)) return;
    if (this.isOpen()) this.closeAll();
  }

  onKeydown(event) {
    if (event.key !== 'Escape') return;
    const link = this.root.querySelector('.menu-list__link[aria-expanded="true"]');
    if (!link) return;
    this.closeAll();
    link.focus();
  }
}

function init() {
  // No viewport check here on purpose. Gating setup on matchMedia meant a load
  // where the viewport was not measurable yet -- a background tab, a window
  // opened narrow and then maximised -- left the menu permanently inert, with
  // nothing to re-run it. The width is checked per click instead.
  for (const root of document.querySelectorAll('header-menu')) {
    // Marked with a property, not a data attribute. The theme hydrates the
    // header through morph.js, which patches this element in place against
    // server HTML that has no such attribute: the listener survives, the
    // attribute does not. A data attribute therefore read as "never
    // initialised" on the next pass and attached a second controller to the
    // same element, so one click was handled twice. A property is invisible to
    // morph and survives with the element.
    if (root.fcMegaMenu) continue;
    root.fcMegaMenu = new FcMegaMenu(root);
  }
}

init();

// The header re-renders through the Section Rendering API (theme editor edits,
// cart drawer refreshes), which replaces the markup and drops the instance.
document.addEventListener('shopify:section:load', init);
